'use client';

import { IconDots } from '@tabler/icons-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { useState, useCallback } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'usehooks-ts';
import { Button } from '@/components/ui/button';

export type Action = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onSelect?: () => void;
  closeDelay?: number;
};

type Props = {
  actions: Action[];
  trigger?: React.ReactNode;
  mobileQuery?: string;
  className?: string;
};

export function ResponsiveActionsMenu({ actions, trigger, mobileQuery = '(max-width: 767px)', className }: Props) {
  const [open, setOpen] = useState(false);

  const isMobile = useMediaQuery(mobileQuery);

  const handleSelect = useCallback((a: Action) => {
    a.onSelect?.();
    if (a.closeDelay) {
      setTimeout(() => setOpen(false), a.closeDelay);
    } else {
      setOpen(false);
    }
  }, []);

  const DefaultTrigger = (
    <Button
      variant='ghost'
      size='icon'
      className={clsx('h-[32px] w-[32px] hover:bg-transparent', className)}
      aria-label='Открыть меню'
    >
      <IconDots size={20} className='text-icon-default' />
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger ?? DefaultTrigger}</DrawerTrigger>

        <DrawerContent className='rounded-t-[20px] shadow-[0_-8px_32px_rgba(0,0,0,0.12)]'>
          <DrawerTitle className='hidden'>menu</DrawerTitle>

          <div className='max-h-[60vh] overflow-y-auto px-[8px] py-[8px]'>
            {actions.map((a) => (
              <Button
                key={a.key}
                variant='ghost'
                onClick={() => handleSelect(a)}
                className='w-full h-auto justify-start gap-[14px] px-[12px] py-[13px] text-[16px] font-normal text-text-primary-day'
              >
                {a.icon && (
                  <span key={`icon-${a.label}`} className='shrink-0 text-icon-default animate-fade-in'>
                    {a.icon}
                  </span>
                )}
                <span key={`label-${a.label}`} className='text-start animate-fade-in'>
                  {a.label}
                </span>
              </Button>
            ))}
          </div>

          <div className='h-4' />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger ?? DefaultTrigger}</DropdownMenuTrigger>

      <DropdownMenuContent
        side='bottom'
        align='end'
        sideOffset={8}
        className={clsx(
          'min-w-[200px] rounded-[14px] bg-white overflow-hidden p-[6px] gap-[2px]',
          'border border-primary-stroke-day',
          'shadow-[0px_8px_24px_rgba(0,0,0,0.10)]',
          'data-[state=open]:animate-menu-in data-[state=closed]:animate-menu-out',
        )}
      >
        {actions.map((a) => (
          <DropdownMenuItem
            key={a.key}
            onSelect={(e) => {
              e.preventDefault();
              handleSelect(a);
            }}
            className='flex items-center gap-[10px] px-[10px] py-[9px] rounded-[10px] cursor-pointer text-[14px] text-text-primary-day hover:bg-bg2-day transition-colors'
          >
            {a.icon && (
              <span key={`icon-${a.label}`} className='shrink-0 text-icon-default animate-fade-in'>
                {a.icon}
              </span>
            )}
            <span key={`label-${a.label}`} className='animate-fade-in'>
              {a.label}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
